import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import styles from './Timeline.module.scss';
import marker from '../asset/marker.svg';
import { Mode, DragInfo } from '../types';
const DRAG_THRESHOLD = 5; // pixels to determine if it's a drag vs click
const TIMELINE_LENGTH = 48;

type TimelineProps = {
  selectedMode: Mode | undefined;
  dayOfWeek: number;
  dayDate?: Date;
  currentInteractionMode: string;
  setCurrentInteractionMode: (val: string) => void;
  handleTimelineMatrix: (dayIndex: number, newTimeline: Mode[]) => void;
  handleMarkerMatrix: (dayIndex: number, newMarkers: number[]) => void;
  initialSegments?: Mode[];
  initialMarkers?: number[];
  onDayClick?: () => void;
  isSourceDay?: boolean;
  isTargetDay?: boolean;
  isInCopyMode?: boolean;
};

const DAYS = [
  'DIMANCHE',
  'LUNDI',
  'MARDI',
  'MERCREDI',
  'JEUDI',
  'VENDREDI',
  'SAMEDI',
];

const getDayName = (dayNumber: number): string => {
  return DAYS[dayNumber];
};

const Timeline = ({
  dayOfWeek,
  dayDate,
  selectedMode,
  currentInteractionMode,
  setCurrentInteractionMode,
  handleTimelineMatrix,
  handleMarkerMatrix,
  initialMarkers,
  initialSegments,
  onDayClick,
  isSourceDay,
  isTargetDay,
  isInCopyMode,
}: TimelineProps) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const [segments, setSegments] = useState(initialSegments || []);
  const segmentsRef = useRef(segments);
  const [markers, setMarkers] = useState(() => {
    const initialMarkersArray = initialMarkers || [0];
    return initialMarkersArray.includes(0)
      ? initialMarkersArray
      : [0, ...initialMarkersArray];
  });

  // Synchroniser le state local avec les props quand elles changent (pour le collage)
  useEffect(() => {
    if (initialSegments && initialSegments.length > 0) {
      setSegments([...initialSegments]);
    }
  }, [initialSegments]);

  useEffect(() => {
    if (initialMarkers && initialMarkers.length > 0) {
      const markersWithZero = initialMarkers.includes(0)
        ? initialMarkers
        : [0, ...initialMarkers];
      setMarkers([...markersWithZero]);
    }
  }, [initialMarkers]);

  const [clickStartPos, setClickStartPos] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const [dragInfo, setDragInfo] = useState<DragInfo>({
    isDragging: false,
    markerIndex: null,
    currentPosition: null,
    adjacentMarkers: { left: null, right: null },
    dragDirection: null,
    initialMode: null,
  });

  const dragInfoRef = useRef(dragInfo);

  useEffect(() => {
    dragInfoRef.current = dragInfo;
  }, [dragInfo]);

  useEffect(() => {
    segmentsRef.current = segments;
  }, [segments]);

  const findNearestMarker = useCallback(
    (position: number): number | null => {
      if (markers.length === 0) return null;

      let nearestMarker: number | null = null;
      let minDistance = Infinity;

      for (const marker of markers) {
        const distance = Math.abs(marker - position);
        if (distance < minDistance) {
          minDistance = distance;
          nearestMarker = marker;
        }
      }
      return minDistance <= 3 ? nearestMarker : null;
    },
    [markers]
  );

  const handleAddMarker = useCallback(
    (position: number) => {
      if (position > 0 && !markers.includes(position)) {
        const newMarkers = [...markers, position].sort((a, b) => a - b);
        setMarkers(newMarkers);
        handleMarkerMatrix(dayOfWeek, newMarkers); // Update parent immediately
      }
    },
    [markers, handleMarkerMatrix, dayOfWeek]
  );
  const handleRemoveMarker = useCallback(
    (position: number) => {
      // Ne pas supprimer le marqueur 0
      if (position === 0) return;

      const sortedMarkers = [...markers].sort((a, b) => a - b);
      const removedMarkerIndex = sortedMarkers.indexOf(position);
      const nextMarker =
        sortedMarkers[removedMarkerIndex + 1] || TIMELINE_LENGTH;
      const modeToApply = segments[position - 1];
      const newSegments = [...segments];
      for (let i = position; i < nextMarker; i++) {
        newSegments[i] = modeToApply;
      }
      const newMarkers = markers.filter((marker) => marker !== position);
      setMarkers(newMarkers);
      setSegments(newSegments);
      handleMarkerMatrix(dayOfWeek, newMarkers);
      handleTimelineMatrix(dayOfWeek, newSegments);
    },
    [markers, segments, handleMarkerMatrix, handleTimelineMatrix, dayOfWeek]
  );

  const handleMouseMove = useCallback(
    (e: globalThis.MouseEvent) => {
      const timeline = timelineRef.current;
      if (!timeline || !dragInfoRef.current.isDragging) return;

      if (clickStartPos) {
        const deltaX = e.clientX - clickStartPos.x;
        const deltaY = e.clientY - clickStartPos.y;
        const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

        // If we exceed the drag threshold and we're in remove-marker mode, switch to drag mode
        if (
          distance > DRAG_THRESHOLD &&
          currentInteractionMode === 'remove-marker'
        ) {
          setCurrentInteractionMode('drag-marker');
        }
      }

      const rect = timeline.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const width = rect.width;
      const cursorPosition = Math.max(
        1,
        Math.min(TIMELINE_LENGTH, Math.round((x / width) * TIMELINE_LENGTH))
      );

      const {
        adjacentMarkers,
        currentPosition: prevPosition,
        initialMode,
      } = dragInfoRef.current;

      let newPosition = cursorPosition;
      if (adjacentMarkers.left !== null) {
        const minLeftPosition = adjacentMarkers.left + 1;
        if (newPosition <= minLeftPosition) {
          newPosition = minLeftPosition;
        }
      }
      if (adjacentMarkers.right !== null) {
        const maxRightPosition = Math.min(
          adjacentMarkers.right - 1,
          TIMELINE_LENGTH - 1
        );
        if (newPosition >= maxRightPosition) {
          newPosition = maxRightPosition;
        }
      }

      const direction = newPosition > (prevPosition || 0) ? 'right' : 'left';
      if (prevPosition !== null && prevPosition !== newPosition) {
        setSegments((prevSegments) => {
          const newSegments = [...prevSegments];
          const startSegment = Math.min(prevPosition, newPosition);
          const endSegment = Math.max(prevPosition, newPosition);
          const modeToApply =
            direction === 'right'
              ? initialMode || prevSegments[startSegment - 1]
              : prevSegments[endSegment];

          if (modeToApply) {
            for (let i = startSegment; i < endSegment; i++) {
              newSegments[i] = modeToApply;
            }
          }
          return newSegments;
        });
      }

      setDragInfo((prev) => ({
        ...prev,
        currentPosition: newPosition,
        dragDirection: direction,
      }));
    },
    [clickStartPos, setCurrentInteractionMode, currentInteractionMode]
  );

  const handleMouseUp = useCallback(() => {
    if (!dragInfoRef.current.isDragging) return;

    if (clickStartPos) {
      const dragMarkerIndex = dragInfoRef.current.markerIndex;
      const deltaX = (window.event as MouseEvent).clientX - clickStartPos.x;
      const deltaY = (window.event as MouseEvent).clientY - clickStartPos.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

      // If it was a click (movement less than threshold) and we're in remove mode
      if (
        distance <= DRAG_THRESHOLD &&
        currentInteractionMode === 'remove-marker' &&
        dragMarkerIndex !== null
      ) {
        handleRemoveMarker(markers[dragMarkerIndex]);
        setClickStartPos(null);
        setDragInfo({
          isDragging: false,
          markerIndex: null,
          currentPosition: null,
          adjacentMarkers: { left: null, right: null },
          dragDirection: null,
          initialMode: null,
        });
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        return;
      }
    }

    const { currentPosition, markerIndex: draggedMarkerIndex } =
      dragInfoRef.current;
    if (currentPosition !== null && draggedMarkerIndex !== null) {
      const oldPosition = markers[draggedMarkerIndex];

      // Ne pas permettre de déplacer le marqueur 0
      if (oldPosition === 0) {
        setClickStartPos(null);
        setDragInfo({
          isDragging: false,
          markerIndex: null,
          currentPosition: null,
          adjacentMarkers: { left: null, right: null },
          dragDirection: null,
          initialMode: null,
        });
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        return;
      }

      const newMarkers = [...markers];
      newMarkers[draggedMarkerIndex] = currentPosition;
      const newSortedMarkers = newMarkers.sort((a, b) => a - b);
      setMarkers(newSortedMarkers);
      handleMarkerMatrix(dayOfWeek, newSortedMarkers);

      const newSegments = [...segmentsRef.current];
      setSegments(newSegments);
      handleTimelineMatrix(dayOfWeek, newSegments);
    }

    setClickStartPos(null);
    setDragInfo({
      isDragging: false,
      markerIndex: null,
      currentPosition: null,
      adjacentMarkers: { left: null, right: null },
      dragDirection: null,
      initialMode: null,
    });

    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }, [
    currentInteractionMode,
    clickStartPos,
    markers,
    handleRemoveMarker,
    handleMouseMove,
    dayOfWeek,
    handleMarkerMatrix,
    handleTimelineMatrix,
  ]);

  const handleSegmentClick = useCallback(
    (index: number, e?: React.MouseEvent) => {
      // Si on est en mode copie, empêcher la propagation et laisser le clic parent gérer
      if (currentInteractionMode === 'copy-day') {
        if (e) {
          e.stopPropagation();
        }
        return;
      }

      if (currentInteractionMode === 'color') {
        if (!selectedMode) return;
        const newSegments = [...segments];
        const allMarkers = [0, ...markers, TIMELINE_LENGTH];
        let startBound = 0;
        let endBound = TIMELINE_LENGTH - 1;
        for (let i = 0; i < allMarkers.length - 1; i++) {
          if (index >= allMarkers[i] && index < allMarkers[i + 1]) {
            startBound = allMarkers[i];
            endBound = allMarkers[i + 1] - 1;
            break;
          }
        }
        for (let i = startBound; i <= endBound; i++) {
          newSegments[i] = selectedMode;
        }
        setSegments(newSegments);
        handleTimelineMatrix(dayOfWeek, newSegments); // Update parent immediately
      } else if (currentInteractionMode === 'add-marker') {
        handleAddMarker(index);
      } else if (currentInteractionMode === 'remove-marker') {
        const nearestMarker = findNearestMarker(index);
        if (nearestMarker !== null) {
          handleRemoveMarker(nearestMarker);
        }
      }
    },
    [
      currentInteractionMode,
      selectedMode,
      markers,
      segments,
      handleAddMarker,
      handleRemoveMarker,
      findNearestMarker,
      handleTimelineMatrix,
      dayOfWeek,
    ]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, markerIndex: number) => {
      const timeline = timelineRef.current;
      if (!timeline) return;

      // Ne pas permettre de déplacer le marqueur 0 (début de timeline)
      if (markers[markerIndex] === 0) {
        return;
      }

      e.preventDefault();

      // Store the initial click position
      setClickStartPos({ x: e.clientX, y: e.clientY });

      const rect = timeline.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const width = rect.width;
      const position = Math.max(
        1,
        Math.min(TIMELINE_LENGTH, Math.round((x / width) * TIMELINE_LENGTH))
      );

      const markerPositions = [...markers].sort((a, b) => a - b);
      const currentMarkerIndex = markerPositions.indexOf(markers[markerIndex]);
      const leftMarker =
        currentMarkerIndex > 0 ? markerPositions[currentMarkerIndex - 1] : null;
      const rightMarker =
        currentMarkerIndex < markerPositions.length - 1
          ? markerPositions[currentMarkerIndex + 1]
          : null;

      const initialMode =
        position > 0 ? segmentsRef.current[position - 1] : null;

      setDragInfo({
        isDragging: true,
        markerIndex,
        currentPosition: position,
        adjacentMarkers: {
          left: leftMarker,
          right: rightMarker,
        },
        dragDirection: null,
        initialMode,
      });

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [markers, handleMouseMove, handleMouseUp]
  );

  // Fonction spécifique pour gérer le clic sur le jour en mode copie
  const handleDayClick = useCallback(
    (e: React.MouseEvent) => {
      if (currentInteractionMode === 'copy-day' && onDayClick) {
        e.preventDefault();
        e.stopPropagation();
        onDayClick();
      }
    },
    [currentInteractionMode, onDayClick]
  );

  useEffect(() => {
    if (dragInfo.isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [dragInfo.isDragging, handleMouseMove, handleMouseUp]);

  const renderHours = useMemo(() => {
    const hours = [];
    for (let i = 0; i < 25; i++) {
      hours.push(
        <div
          key={i}
          className={styles.hourMarker}
          style={{ gridColumnStart: i * 2 + 1 }}
        >
          {i}h
        </div>
      );
    }
    return hours;
  }, []);

  const renderMarkers = useMemo(() => {
    return markers
      .filter((position) => position !== 0) // Masquer le marqueur à la position 0
      .map((position, index) => {
        // Retrouver l'index original du marqueur dans la liste complète
        const originalIndex = markers.indexOf(position);
        const isDragging =
          dragInfo.isDragging && dragInfo.markerIndex === originalIndex;
        const currentPosition = isDragging
          ? dragInfo.currentPosition
          : position;

        return (
          <div
            key={originalIndex}
            className={styles.marker}
            data-testid="timeline-marker"
            style={{
              left: `${
                ((currentPosition ?? position) / TIMELINE_LENGTH) * 100
              }%`,
            }}
            onMouseDown={(e) => handleMouseDown(e, originalIndex)}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              // Only remove marker on click if we're in remove mode and haven't just finished dragging
              // Ne pas supprimer le marqueur 0
              if (
                currentInteractionMode === 'remove-marker' &&
                !dragInfo.isDragging &&
                position !== 0
              ) {
                handleRemoveMarker(position);
              }
            }}
          >
            {' '}
            <img src={marker} alt="marker icon" draggable={false} />
          </div>
        );
      });
  }, [
    markers,
    dragInfo,
    currentInteractionMode,
    handleMouseDown,
    handleRemoveMarker,
  ]);

  const renderSegments = useMemo(() => {
    return segments.map((mode, index) => {
      return (
        <div
          key={index}
          data-testid="timeline-segment"
          className={styles.segment}
          style={{ backgroundColor: mode ? mode.color : '#f0f0f0' }}
          onClick={(e) => {
            handleSegmentClick(index, e);
          }}
        />
      );
    });
  }, [segments, handleSegmentClick]);

  return (
    <div
      className={`${styles.container} ${isInCopyMode ? styles.copyMode : ''} ${
        isSourceDay ? styles.sourceDay : ''
      } ${isTargetDay ? styles.targetDay : ''}`}
      onClick={handleDayClick}
      style={{
        backgroundColor: isInCopyMode
          ? 'rgb(211, 211, 211, 0.1)'
          : isTargetDay
            ? 'rgb(211, 211, 211, 0.1)'
            : 'transparent',

        cursor: isInCopyMode ? 'pointer' : 'default',
        border: isSourceDay
          ? '3px solid gray'
          : isTargetDay
            ? '2px solid rgb(211, 211, 211, 0.1)'
            : isInCopyMode
              ? '2px dashed #ccc'
              : 'none',
        borderRadius: isInCopyMode ? '8px' : '0',
        transition: 'all 0.3s ease',
        position: 'relative',
      }}
    >
      <label
        className={styles.dayLabel}
        style={{ pointerEvents: isInCopyMode ? 'none' : 'auto' }}
      >
        <div
          className={styles.dayCheckbox}
          style={{
            fontWeight: isSourceDay ? 900 : 'normal',
            color: 'inherit',
          }}
        >
          {isTargetDay && (
            <span style={{ marginRight: '10px' }}>
              <svg
                width="20"
                height="20"
                viewBox="0 0 36 31"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M21.82 3.03442H5.30127C3.64441 3.03442 2.30127 4.37757 2.30127 6.03442V26.3592C2.30127 28.0161 3.64442 29.3592 5.30127 29.3592H25.3262C26.9831 29.3592 28.3262 28.0161 28.3262 26.3592V20.0681"
                  stroke="#646464"
                  stroke-width="3"
                  stroke-linecap="round"
                />
                <path
                  d="M10.1084 14.0426L16.8005 19.7862L33.5309 5.42725"
                  stroke="#646464"
                  stroke-width="3"
                  stroke-linecap="round"
                />
              </svg>
            </span>
          )}
          {dayDate
            ? new Intl.DateTimeFormat('fr').format(dayDate)
            : getDayName(dayOfWeek)}
        </div>
      </label>
      <div
        className={styles.timelineContainer}
        style={{
          pointerEvents: isInCopyMode ? 'none' : 'auto', // Désactive les interactions en mode copie
        }}
      >
        <div className={styles.hoursHeader}>{renderHours}</div>
        <div className={styles.timelineRow}>
          <div className={styles.timeline} ref={timelineRef}>
            {renderSegments}
            {renderMarkers}
          </div>
        </div>
      </div>
    </div>
  );
};
export default Timeline;
